/**
 * slots.ts — pinned-slot geometry for the IMAGE-based "beads" bases (BAG CHARM + KEYCHAIN).
 *
 * Unlike the bracelet/necklace ring (geometry.ts, position = an angle t∈[0,1)), these are a
 * fixed beaded-chain base IMAGE with charms attached at a fixed set of hard-pinned coordinate
 * slots. The base PNG is the SAME dimensions/framing across every colour palette, so one slot
 * set serves all palettes of that base. Each beads base has its own slot set, carabiner
 * closure anchor(s) and orientation focal — see BEADS_LAYOUTS / beadsLayoutFor().
 *
 * Coordinates are NORMALISED to the base image (x,y ∈ [0,1], origin top-left), taken off the
 * merchant's dotted reference. A placed charm stores its SLOT INDEX in CharmInstance.t (still:
 * null = tray, non-null = placed — so pricing/serialization are unchanged). No DOM, no Shopify.
 */
import type { BaseKind } from './state';

export interface BeadSlot {
  /** Normalised position on the base image (0..1, origin top-left). */
  x: number;
  y: number;
  /** Optional explicit charm-top rotation in DEGREES (clockwise). When omitted the
   *  angle is derived so the charm's top points toward BAGCHAIN_FOCAL (see slotAngleDeg). */
  angle?: number;
  /** Inner/upper slots: the chain is BELOW the dot, so the charm flips 180° to hang
   *  upside-down and attach to the chain under it. Added on top of the computed angle. */
  flip?: boolean;
  /** Fine rotation tweak in DEGREES (+ = clockwise), added after focal angle + flip. */
  nudge?: number;
}

/** The rendered (object-contain) base-image rectangle in stage pixels. */
export interface SlotBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The point (normalised) a charm's TOP orients toward when a slot has no explicit
 * `angle` — i.e. "top toward the beaded chain". A focal in the upper-middle makes
 * left-arm charms point right, bottom charms point up, right-arm charms point left,
 * so every charm dangles outward off the chain. Tunable; per-slot `angle` overrides.
 */
export const BAGCHAIN_FOCAL = { x: 0.5, y: 0.30 };

/**
 * Attach points along the bead chain, ordered left-carabiner → bottom → right-carabiner.
 * SEED VALUES estimated from the dotted reference — fine-tune with the debug slot overlay
 * (canvas DEBUG_SLOTS) against the production artwork. Same set for every palette.
 */
export const BAGCHAIN_SLOTS: BeadSlot[] = [
  /* 0 */ { x: 0.0284, y: 0.0955 },
  /* 1 */ { x: 0.0668, y: 0.0857, flip: true, nudge: -10 },
  /* 2 */ { x: 0.0409, y: 0.2110, nudge: -15 },
  /* 3 */ { x: 0.0996, y: 0.3436, flip: true, nudge: -5 },
  /* 4 */ { x: 0.0843, y: 0.4815 },
  /* 5 */ { x: 0.1695, y: 0.5690, flip: true },
  /* 6 */ { x: 0.1277, y: 0.6078 },
  /* 7 */ { x: 0.2642, y: 0.7285, flip: true },
  /* 8 */ { x: 0.2202, y: 0.7637 },
  /* 9 */ { x: 0.3293, y: 0.7862, flip: true },
  /* 10 */ { x: 0.3029, y: 0.8429 },
  /* 11 */ { x: 0.4555, y: 0.8904, flip: true },
  /* 12 */ { x: 0.4479, y: 0.9626 },
  /* 13 */ { x: 0.5459, y: 0.8834, flip: true },
  /* 14 */ { x: 0.6244, y: 0.8508, flip: true },
  /* 15 */ { x: 0.6420, y: 0.9225, nudge: 8 },
  /* 16 */ { x: 0.7165, y: 0.8456 },
  /* 17 */ { x: 0.7480, y: 0.7373, flip: true, nudge: -10 },
  /* 18 */ { x: 0.8394, y: 0.6712 },
  /* 19 */ { x: 0.8380, y: 0.5455, flip: true },
  /* 20 */ { x: 0.8683, y: 0.5542 },
  /* 21 */ { x: 0.8605, y: 0.4531 },
  /* 22 */ { x: 0.8896, y: 0.3457, flip: true },
  /* 23 */ { x: 0.9438, y: 0.3790, nudge: 10 },
  /* 24 */ { x: 0.9233, y: 0.1085, flip: true, nudge: 20 },
  /* 25 */ { x: 0.9607, y: 0.1096 },
];

/**
 * Closure (carabiner) attach points on the base image: [left end, right end]. The clasp
 * PNG is centred (translate -50%,-50%) on each point. Kept SEPARATE from BAGCHAIN_SLOTS so
 * the carabiners can be nudged independently of the nearest charm dot — calibrate them with
 * the canvas closure-calibrate overlay (BEADS_CALIBRATE_CLOSURES), then paste the result here.
 */
export const BAGCHAIN_CLOSURE_SLOTS: BeadSlot[] = [
  /* left  */ { x: 0.0457, y: 0.0479, angle: 205 },
  /* right */ { x: 0.9469, y: 0.0558, angle: 145 },
];

// ─── Keychain (a beaded LOOP with one carabiner at the top) ──────────────────
/**
 * Keychain focal: the loop CENTRE. With no per-slot `angle`/`flip`, every charm's top points
 * toward the centre so the charms dangle OUTWARD all the way around the loop (no inward flips).
 */
export const KEYCHAIN_FOCAL = { x: 0.5, y: 0.55 };

/**
 * 15 SEED attach points spread around the loop (gap left at the top for the carabiner),
 * ordered clockwise from the upper-right. No flips — calibrate against the real artwork with
 * the canvas slot overlay (BEADS_CALIBRATE), then paste the result back here.
 */
export const KEYCHAIN_SLOTS: BeadSlot[] = [
  /* 0  */ { x: 0.3123, y: 0.1681 },
  /* 1  */ { x: 0.7166, y: 0.1568 },
  /* 2  */ { x: 0.7952, y: 0.2863 },
  /* 3  */ { x: 0.8300, y: 0.3880 },
  /* 4  */ { x: 0.8475, y: 0.4803 },
  /* 5  */ { x: 0.9038, y: 0.5892 },
  /* 6  */ { x: 0.8838, y: 0.7217 },
  /* 7  */ { x: 0.8223, y: 0.8146 },
  /* 8  */ { x: 0.6081, y: 0.9486 },
  /* 9  */ { x: 0.3551, y: 0.9346 },
  /* 10 */ { x: 0.1842, y: 0.8293 },
  /* 11 */ { x: 0.1189, y: 0.7223 },
  /* 12 */ { x: 0.0892, y: 0.5867 },
  /* 13 */ { x: 0.1454, y: 0.4050 },
  /* 14 */ { x: 0.2213, y: 0.2908 },
];

/** Keychain carabiner: a single clasp pinned at the top of the loop (rotated 180° around its
 *  top-centre attach point so the ring sits above the chain). */
export const KEYCHAIN_CLOSURE_SLOTS: BeadSlot[] = [
  /* 0 */ { x: 0.5065, y: 0.0438, angle: 180 },
];

/** Per-base beads geometry: charm slots, carabiner anchor(s) and the orientation focal. */
export interface BeadsLayout {
  slots: BeadSlot[];
  closures: BeadSlot[];
  focal: { x: number; y: number };
}
const BEADS_LAYOUTS: Partial<Record<BaseKind, BeadsLayout>> = {
  bag_charm: { slots: BAGCHAIN_SLOTS, closures: BAGCHAIN_CLOSURE_SLOTS, focal: BAGCHAIN_FOCAL },
  keychain:  { slots: KEYCHAIN_SLOTS, closures: KEYCHAIN_CLOSURE_SLOTS, focal: KEYCHAIN_FOCAL },
};
/** The beads geometry for a base (falls back to the bag charm for non-beads bases). */
export function beadsLayoutFor(base: BaseKind): BeadsLayout {
  return BEADS_LAYOUTS[base] ?? BEADS_LAYOUTS.bag_charm!;
}

/** The rendered content rect of an object-contain image inside a `cw`×`ch` container. */
export function containBox(cw: number, ch: number, imgW: number, imgH: number): SlotBox {
  if (imgW <= 0 || imgH <= 0) return { left: 0, top: 0, width: cw, height: ch };
  const scale = Math.min(cw / imgW, ch / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return { left: (cw - w) / 2, top: (ch - h) / 2, width: w, height: h };
}

/** A slot's pixel position within the rendered base-image box. */
export function slotPx(slot: BeadSlot, box: SlotBox): { x: number; y: number } {
  return { x: box.left + slot.x * box.width, y: box.top + slot.y * box.height };
}

/**
 * Charm-top rotation (degrees, clockwise) for a slot. Explicit `angle` wins; otherwise the
 * charm's top is rotated to point from the slot toward `focal` (the base's orientation focal),
 * so the charm dangles outward off the chain. The charm body defaults to hanging straight down
 * (rotation 0 = top up), so this orients it along the chain normal.
 */
export function slotAngleDeg(slot: BeadSlot, focal: { x: number; y: number }): number {
  let deg: number;
  if (slot.angle != null) {
    deg = slot.angle;
  } else {
    const dx = focal.x - slot.x;
    const dy = focal.y - slot.y;
    // Rotate "up" (0,-1) to align with (dx,dy): clockwise angle = atan2(dx, -dy).
    deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  }
  if (slot.flip) deg += 180;
  return deg + (slot.nudge ?? 0);
}

/**
 * Nearest UNOCCUPIED slot to a dropped point, within `tolPx`. Returns the slot index, or null
 * when the closest free slot is out of tolerance (drop is rejected / parked). `occupied` are
 * the slot indices already taken by other placed charms; `slots` is the base's slot set.
 */
export function nearestFreeSlot(
  px: number,
  py: number,
  occupied: number[],
  box: SlotBox,
  tolPx: number,
  slots: BeadSlot[],
): number | null {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < slots.length; i++) {
    if (occupied.indexOf(i) !== -1) continue;
    const p = slotPx(slots[i], box);
    const d = Math.hypot(px - p.x, py - p.y);
    if (d < bestD) { bestD = d; best = i; }
  }
  return best >= 0 && bestD <= tolPx ? best : null;
}
